import styles from './DocsContent.module.css'

const colors = [
  { n: 0, name: 'Black', hex: '#000000' },
  { n: 1, name: 'Dark Blue', hex: '#1D2B53' },
  { n: 2, name: 'Dark Purple', hex: '#7E2553' },
  { n: 3, name: 'Dark Green', hex: '#008751' },
  { n: 4, name: 'Brown', hex: '#AB5236' },
  { n: 5, name: 'Dark Gray', hex: '#5F574F' },
  { n: 6, name: 'Light Gray', hex: '#C2C3C7' },
  { n: 7, name: 'White', hex: '#FFF1E8' },
  { n: 8, name: 'Red', hex: '#FF004D' },
  { n: 9, name: 'Orange', hex: '#FFA300' },
  { n: 10, name: 'Yellow', hex: '#FFEC27' },
  { n: 11, name: 'Green', hex: '#00E436' },
  { n: 12, name: 'Blue', hex: '#29ADFF' },
  { n: 13, name: 'Lavender', hex: '#83769C' },
  { n: 14, name: 'Pink', hex: '#FF77A8' },
  { n: 15, name: 'Light Peach', hex: '#FFCCAA' },
]

function Code({ children }: { children: string }) {
  return <code className={styles.inlineCode}>{children}</code>
}

function CodeBlock({ children }: { children: string }) {
  return <pre className={styles.codeBlock}>{children}</pre>
}

function Note({ children }: { children: React.ReactNode }) {
  return <div className={styles.note}>{children}</div>
}

function FuncEntry({ name, sig, children }: { name: string; sig: string; children: React.ReactNode }) {
  return (
    <div className={styles.funcEntry}>
      <div className={styles.funcSig}>
        <span className={styles.funcName}>{name}</span>{sig}
      </div>
      {children}
    </div>
  )
}

export function DocsContent() {
  return (
    <div className={styles.docs}>
      {/* Getting Started */}
      <section id="getting-started" className={styles.section}>
        <h2 className={styles.sectionTitle}>Getting Started</h2>
        <div className={styles.prose}>
          <p>
            FC0 games are JavaScript programs that define three functions:
            <Code>_init()</Code>, <Code>_update()</Code>, and <Code>_draw()</Code>.
          </p>
          <p>Here's a minimal example that moves a sprite with the d-pad:</p>
        </div>
        <CodeBlock>{`function _init() {
  x = 64
  y = 64
}

function _update() {
  if (btn(0)) x -= 1
  if (btn(1)) x += 1
  if (btn(2)) y -= 1
  if (btn(3)) y += 1
}

function _draw() {
  cls()
  spr(1, x, y)
}`}</CodeBlock>
        <Note>
          The screen is <strong>128x128 pixels</strong>, runs at <strong>60fps</strong>,
          and your code executes in a sandboxed iframe.
        </Note>
      </section>

      {/* Lifecycle */}
      <section id="lifecycle" className={styles.section}>
        <h2 className={styles.sectionTitle}>Lifecycle Functions</h2>
        <div className={styles.prose}>
          <FuncEntry name="_init" sig="()">
            <p className={styles.funcDesc}>Called once at start. Use this to set up initial state.</p>
          </FuncEntry>
          <FuncEntry name="_update" sig="()">
            <p className={styles.funcDesc}>Called every frame for game logic (60fps). Runs before <Code>_draw()</Code>.</p>
          </FuncEntry>
          <FuncEntry name="_draw" sig="()">
            <p className={styles.funcDesc}>Called every frame after <Code>_update()</Code> for rendering.</p>
          </FuncEntry>
        </div>
      </section>

      {/* Color Palette */}
      <section id="color-palette" className={styles.section}>
        <h2 className={styles.sectionTitle}>Color Palette</h2>
        <div className={styles.prose}>
          <p>FC0 uses the PICO-8 16-color palette:</p>
        </div>
        <table className={styles.table}>
          <thead>
            <tr><th>#</th><th>Color</th><th>Hex</th><th>Swatch</th></tr>
          </thead>
          <tbody>
            {colors.map(c => (
              <tr key={c.n}>
                <td>{c.n}</td>
                <td>{c.name}</td>
                <td><Code>{c.hex}</Code></td>
                <td>
                  <span
                    className={styles.swatch}
                    style={{ backgroundColor: c.hex }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Button Reference */}
      <section id="button-reference" className={styles.section}>
        <h2 className={styles.sectionTitle}>Button Reference</h2>
        <table className={styles.table}>
          <thead>
            <tr><th>ID</th><th>Name</th><th>Keys</th></tr>
          </thead>
          <tbody>
            <tr><td>0</td><td>Left</td><td>Arrow Left, A</td></tr>
            <tr><td>1</td><td>Right</td><td>Arrow Right, D</td></tr>
            <tr><td>2</td><td>Up</td><td>Arrow Up, W</td></tr>
            <tr><td>3</td><td>Down</td><td>Arrow Down, S</td></tr>
            <tr><td>4</td><td>B</td><td>Z, J</td></tr>
            <tr><td>5</td><td>A</td><td>X, K</td></tr>
            <tr><td>6</td><td>Start</td><td>Enter, Space</td></tr>
            <tr><td>7</td><td>Select</td><td>Shift</td></tr>
          </tbody>
        </table>
      </section>

      {/* Constraints */}
      <section id="constraints" className={styles.section}>
        <h2 className={styles.sectionTitle}>Constraints</h2>
        <table className={styles.table}>
          <thead>
            <tr><th>Property</th><th>Value</th></tr>
          </thead>
          <tbody>
            <tr><td>Screen</td><td>128 x 128 pixels</td></tr>
            <tr><td>Colors</td><td>16 (PICO-8 palette)</td></tr>
            <tr><td>Sprites</td><td>256 (8x8px each, on 128x128 sheet)</td></tr>
            <tr><td>Map</td><td>128 x 64 tiles</td></tr>
            <tr><td>SFX</td><td>64 slots, 32 notes each, 4 channels</td></tr>
            <tr><td>Code</td><td>9,999 token limit</td></tr>
            <tr><td>Frame rate</td><td>60fps</td></tr>
          </tbody>
        </table>
      </section>

      {/* Drawing */}
      <section id="drawing" className={styles.section}>
        <h2 className={styles.sectionTitle}>Drawing</h2>
        <div className={styles.prose}>
          <p>All drawing functions operate on the 128x128 screen. Colors are 0-15.</p>
        </div>

        <FuncEntry name="cls" sig="(col?)">
          <p className={styles.funcDesc}>Clear the screen. Default color 0 (black).</p>
          <table className={styles.table}>
            <thead><tr><th>Param</th><th>Type</th><th>Default</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><code>col</code></td><td>number</td><td>0</td><td>Color to fill screen with (0-15)</td></tr>
            </tbody>
          </table>
        </FuncEntry>

        <FuncEntry name="pset" sig="(x, y, col?)">
          <p className={styles.funcDesc}>Set pixel at (x, y).</p>
          <table className={styles.table}>
            <thead><tr><th>Param</th><th>Type</th><th>Default</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><code>x</code></td><td>number</td><td>—</td><td>X coordinate</td></tr>
              <tr><td><code>y</code></td><td>number</td><td>—</td><td>Y coordinate</td></tr>
              <tr><td><code>col</code></td><td>number</td><td>current</td><td>Color (0-15)</td></tr>
            </tbody>
          </table>
        </FuncEntry>

        <FuncEntry name="pget" sig="(x, y)">
          <p className={styles.funcDesc}>Get the color of pixel at (x, y). Returns 0-15.</p>
          <table className={styles.table}>
            <thead><tr><th>Param</th><th>Type</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><code>x</code></td><td>number</td><td>X coordinate</td></tr>
              <tr><td><code>y</code></td><td>number</td><td>Y coordinate</td></tr>
            </tbody>
          </table>
        </FuncEntry>

        <FuncEntry name="color" sig="(col)">
          <p className={styles.funcDesc}>Set the default drawing color (0-15).</p>
        </FuncEntry>

        <FuncEntry name="rect" sig="(x, y, w, h, col?)">
          <p className={styles.funcDesc}>Draw a rectangle outline. x, y = top-left corner; w, h = size.</p>
        </FuncEntry>

        <FuncEntry name="rectfill" sig="(x, y, w, h, col?)">
          <p className={styles.funcDesc}>Draw a filled rectangle.</p>
        </FuncEntry>

        <FuncEntry name="circ" sig="(cx, cy, r, col?)">
          <p className={styles.funcDesc}>Draw a circle outline. cx, cy = center; r = radius.</p>
        </FuncEntry>

        <FuncEntry name="circfill" sig="(cx, cy, r, col?)">
          <p className={styles.funcDesc}>Draw a filled circle.</p>
        </FuncEntry>

        <FuncEntry name="oval" sig="(x, y, w, h, col?)">
          <p className={styles.funcDesc}>Draw an ellipse outline. x, y = top-left of bounding box; w, h = bounding box size.</p>
        </FuncEntry>

        <FuncEntry name="ovalfill" sig="(x, y, w, h, col?)">
          <p className={styles.funcDesc}>Draw a filled ellipse.</p>
        </FuncEntry>

        <FuncEntry name="line" sig="(x0, y0, x1, y1, col?)">
          <p className={styles.funcDesc}>Draw a line from (x0, y0) to (x1, y1).</p>
        </FuncEntry>

        <FuncEntry name="print" sig="(str, x?, y?, col?)">
          <p className={styles.funcDesc}>Print text. 3x5 font, 4px character width. Uppercase only.</p>
        </FuncEntry>

        <FuncEntry name="printXL" sig="(str, x?, y?, col?)">
          <p className={styles.funcDesc}>Print large text. 4x6 font, 5px character width. Uppercase only.</p>
        </FuncEntry>

        <h3 className={styles.subTitle}>Drawing Example</h3>
        <CodeBlock>{`function _draw() {
  cls(1)                        // dark blue background
  rectfill(10, 10, 20, 20, 8)  // red square
  circ(64, 64, 20, 12)         // blue circle
  print("HELLO", 50, 100, 7)   // white text
}`}</CodeBlock>
      </section>

      {/* Input */}
      <section id="input" className={styles.section}>
        <h2 className={styles.sectionTitle}>Input</h2>

        <FuncEntry name="btn" sig="(id)">
          <p className={styles.funcDesc}>Returns <Code>true</Code> if button is currently held down.</p>
        </FuncEntry>

        <FuncEntry name="btnp" sig="(id)">
          <p className={styles.funcDesc}>Returns <Code>true</Code> if button was just pressed this frame (not held previous frame).</p>
        </FuncEntry>

        <h3 className={styles.subTitle}>Button Mapping</h3>
        <table className={styles.table}>
          <thead>
            <tr><th>ID</th><th>Name</th><th>Keys</th></tr>
          </thead>
          <tbody>
            <tr><td>0</td><td>Left</td><td>Arrow Left, A</td></tr>
            <tr><td>1</td><td>Right</td><td>Arrow Right, D</td></tr>
            <tr><td>2</td><td>Up</td><td>Arrow Up, W</td></tr>
            <tr><td>3</td><td>Down</td><td>Arrow Down, S</td></tr>
            <tr><td>4</td><td>B</td><td>Z, J</td></tr>
            <tr><td>5</td><td>A</td><td>X, K</td></tr>
            <tr><td>6</td><td>Start</td><td>Enter, Space</td></tr>
            <tr><td>7</td><td>Select</td><td>Shift</td></tr>
          </tbody>
        </table>

        <CodeBlock>{`if (btn(5)) {     // A button (X or K)
  shoot()
}
if (btnp(6)) {    // Start just pressed
  paused = !paused
}`}</CodeBlock>
      </section>

      {/* Sprites */}
      <section id="sprites" className={styles.section}>
        <h2 className={styles.sectionTitle}>Sprites</h2>
        <Note>
          The sprite sheet is <strong>128x128px</strong>, arranged as a <strong>16x16 grid</strong>.
          Sprites are numbered 0-255, each <strong>8x8px</strong>. Color 0 is transparent.
        </Note>

        <FuncEntry name="spr" sig="(n, x, y, w?, h?, flipX?, flipY?)">
          <p className={styles.funcDesc}>Draw sprite number <Code>n</Code> at position (x, y).</p>
          <table className={styles.table}>
            <thead><tr><th>Param</th><th>Type</th><th>Default</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><code>n</code></td><td>number</td><td>—</td><td>Sprite number (0-255)</td></tr>
              <tr><td><code>x</code></td><td>number</td><td>—</td><td>X position</td></tr>
              <tr><td><code>y</code></td><td>number</td><td>—</td><td>Y position</td></tr>
              <tr><td><code>w</code></td><td>number</td><td>1</td><td>Width in sprite units (8px each)</td></tr>
              <tr><td><code>h</code></td><td>number</td><td>1</td><td>Height in sprite units (8px each)</td></tr>
              <tr><td><code>flipX</code></td><td>boolean</td><td>false</td><td>Flip horizontally</td></tr>
              <tr><td><code>flipY</code></td><td>boolean</td><td>false</td><td>Flip vertically</td></tr>
            </tbody>
          </table>
        </FuncEntry>

        <FuncEntry name="sspr" sig="(sx, sy, sw, sh, dx, dy, dw?, dh?, flipX?, flipY?)">
          <p className={styles.funcDesc}>Draw a region of the sprite sheet with optional scaling.</p>
          <table className={styles.table}>
            <thead><tr><th>Param</th><th>Type</th><th>Default</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><code>sx, sy</code></td><td>number</td><td>—</td><td>Source position on sprite sheet (pixels)</td></tr>
              <tr><td><code>sw, sh</code></td><td>number</td><td>—</td><td>Source size (pixels)</td></tr>
              <tr><td><code>dx, dy</code></td><td>number</td><td>—</td><td>Destination position on screen</td></tr>
              <tr><td><code>dw</code></td><td>number</td><td>sw</td><td>Destination width (for scaling)</td></tr>
              <tr><td><code>dh</code></td><td>number</td><td>sh</td><td>Destination height (for scaling)</td></tr>
              <tr><td><code>flipX</code></td><td>boolean</td><td>false</td><td>Flip horizontally</td></tr>
              <tr><td><code>flipY</code></td><td>boolean</td><td>false</td><td>Flip vertically</td></tr>
            </tbody>
          </table>
        </FuncEntry>
      </section>

      {/* Palette */}
      <section id="palette" className={styles.section}>
        <h2 className={styles.sectionTitle}>Palette</h2>

        <FuncEntry name="pal" sig="(c0?, c1?, p?)">
          <p className={styles.funcDesc}>
            Remap color <Code>c0</Code> to <Code>c1</Code>. Call with no args to reset all remappings.
          </p>
          <table className={styles.table}>
            <thead><tr><th>Param</th><th>Type</th><th>Default</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><code>c0</code></td><td>number</td><td>—</td><td>Original color</td></tr>
              <tr><td><code>c1</code></td><td>number</td><td>—</td><td>Replacement color</td></tr>
              <tr><td><code>p</code></td><td>number</td><td>0</td><td>0 = draw palette, 1 = screen palette</td></tr>
            </tbody>
          </table>
        </FuncEntry>

        <FuncEntry name="palt" sig="(col?, t?)">
          <p className={styles.funcDesc}>
            Set transparency for a color when drawing sprites. Call with no args to reset (only color 0 transparent).
          </p>
          <table className={styles.table}>
            <thead><tr><th>Param</th><th>Type</th><th>Default</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><code>col</code></td><td>number</td><td>—</td><td>Color index (0-15)</td></tr>
              <tr><td><code>t</code></td><td>boolean</td><td>—</td><td>true = transparent, false = opaque</td></tr>
            </tbody>
          </table>
        </FuncEntry>
      </section>

      {/* Camera & Clipping */}
      <section id="camera-clipping" className={styles.section}>
        <h2 className={styles.sectionTitle}>Camera & Clipping</h2>

        <FuncEntry name="camera" sig="(x?, y?)">
          <p className={styles.funcDesc}>
            Offset all subsequent drawing by (-x, -y). Call with no args to reset.
          </p>
        </FuncEntry>

        <FuncEntry name="clip" sig="(x?, y?, w?, h?)">
          <p className={styles.funcDesc}>
            Restrict all drawing to a rectangle. Call with no args to reset to full screen.
          </p>
        </FuncEntry>
      </section>

      {/* Map */}
      <section id="map" className={styles.section}>
        <h2 className={styles.sectionTitle}>Map</h2>
        <Note>
          The map is <strong>128x64 tiles</strong>. Each tile references a sprite number (0-255).
          Tiles with sprite 0 are skipped when drawing.
        </Note>

        <FuncEntry name="map" sig="(celX, celY, sx, sy, celW, celH, layer?)">
          <p className={styles.funcDesc}>Draw a region of the map to the screen.</p>
          <table className={styles.table}>
            <thead><tr><th>Param</th><th>Type</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><code>celX, celY</code></td><td>number</td><td>Top-left tile position on map</td></tr>
              <tr><td><code>sx, sy</code></td><td>number</td><td>Screen position to draw at</td></tr>
              <tr><td><code>celW, celH</code></td><td>number</td><td>Number of tiles to draw (width, height)</td></tr>
              <tr><td><code>layer</code></td><td>number</td><td>Optional bitfield for layer filtering</td></tr>
            </tbody>
          </table>
        </FuncEntry>

        <FuncEntry name="mget" sig="(x, y)">
          <p className={styles.funcDesc}>Get the sprite number of the tile at map position (x, y).</p>
        </FuncEntry>

        <FuncEntry name="mset" sig="(x, y, val)">
          <p className={styles.funcDesc}>Set the sprite number of the tile at map position (x, y).</p>
        </FuncEntry>
      </section>

      {/* Math */}
      <section id="math" className={styles.section}>
        <h2 className={styles.sectionTitle}>Math</h2>

        <table className={styles.table}>
          <thead>
            <tr><th>Function</th><th>Description</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>abs(x)</Code></td><td>Absolute value</td></tr>
            <tr><td><Code>sgn(x)</Code></td><td>Sign of x: -1, 0, or 1</td></tr>
            <tr><td><Code>min(x, y)</Code></td><td>Minimum of two values</td></tr>
            <tr><td><Code>max(x, y)</Code></td><td>Maximum of two values</td></tr>
            <tr><td><Code>mid(x, y, z)</Code></td><td>Middle value. Useful for clamping: <Code>mid(lo, val, hi)</Code></td></tr>
            <tr><td><Code>flr(x)</Code></td><td>Floor (round down)</td></tr>
            <tr><td><Code>ceil(x)</Code></td><td>Ceiling (round up)</td></tr>
            <tr><td><Code>sqrt(x)</Code></td><td>Square root</td></tr>
            <tr><td><Code>sin(x)</Code></td><td>Sine. <strong>x is 0-1, NOT radians.</strong> 0.25 = 90°</td></tr>
            <tr><td><Code>cos(x)</Code></td><td>Cosine. <strong>x is 0-1, NOT radians.</strong> 0.25 = 90°</td></tr>
            <tr><td><Code>atan2(dx, dy)</Code></td><td>Angle from (0,0) to (dx,dy). Returns 0-1.</td></tr>
          </tbody>
        </table>

        <Note>
          <strong>Important:</strong> <Code>sin()</Code> and <Code>cos()</Code> use a 0-1 range
          instead of radians. A full rotation is 1.0, so 0.25 = 90°, 0.5 = 180°, etc.
        </Note>
      </section>

      {/* Random */}
      <section id="random" className={styles.section}>
        <h2 className={styles.sectionTitle}>Random</h2>

        <FuncEntry name="rnd" sig="(x?)">
          <p className={styles.funcDesc}>
            Return a random number in [0, x). If no argument, returns [0, 1). Uses a seeded LCG.
          </p>
        </FuncEntry>

        <FuncEntry name="srand" sig="(seed)">
          <p className={styles.funcDesc}>Set the random seed for reproducible results.</p>
        </FuncEntry>
      </section>

      {/* Strings */}
      <section id="strings" className={styles.section}>
        <h2 className={styles.sectionTitle}>Strings</h2>
        <FuncEntry name="sub" sig="(str, pos, len?)">
          <p className={styles.funcDesc}>
            Extract a substring. <Code>pos</Code> is 0-based. Negative pos counts from end.
          </p>
        </FuncEntry>

        <FuncEntry name="chr" sig="(val)">
          <p className={styles.funcDesc}>Convert a character code to a string.</p>
        </FuncEntry>

        <FuncEntry name="ord" sig="(str, pos?)">
          <p className={styles.funcDesc}>
            Get character code at position. <Code>pos</Code> is 0-based (default 0).
          </p>
        </FuncEntry>
      </section>

      {/* Arrays */}
      <section id="arrays" className={styles.section}>
        <h2 className={styles.sectionTitle}>Arrays</h2>

        <FuncEntry name="add" sig="(arr, val, index?)">
          <p className={styles.funcDesc}>
            Add a value to an array. Optional <Code>index</Code> is 0-based for insertion.
          </p>
        </FuncEntry>

        <FuncEntry name="del" sig="(arr, val)">
          <p className={styles.funcDesc}>Remove first occurrence of <Code>val</Code> from the array.</p>
        </FuncEntry>

        <FuncEntry name="count" sig="(arr)">
          <p className={styles.funcDesc}>Count non-undefined elements in the array.</p>
        </FuncEntry>

        <FuncEntry name="all" sig="(arr)">
          <p className={styles.funcDesc}>Returns the array for iteration.</p>
        </FuncEntry>

        <FuncEntry name="foreach" sig="(arr, fn)">
          <p className={styles.funcDesc}>Call <Code>fn</Code> for each element in the array.</p>
        </FuncEntry>
      </section>

      {/* Audio */}
      <section id="audio" className={styles.section}>
        <h2 className={styles.sectionTitle}>Audio</h2>

        <FuncEntry name="sfx" sig="(n, channel?, offset?)">
          <p className={styles.funcDesc}>Play a sound effect.</p>
          <table className={styles.table}>
            <thead><tr><th>Param</th><th>Type</th><th>Default</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><code>n</code></td><td>number</td><td>—</td><td>SFX number (0-63). -1 to stop channel (or all). -2 to stop looping.</td></tr>
              <tr><td><code>channel</code></td><td>number</td><td>auto</td><td>Channel 0-3, or omit for auto-assignment</td></tr>
              <tr><td><code>offset</code></td><td>number</td><td>0</td><td>Start note (0-31)</td></tr>
            </tbody>
          </table>
        </FuncEntry>

        <Note>
          64 SFX slots, 32 notes each, 4 channels, 8 waveforms, 8 effects.
        </Note>
      </section>

      {/* Debug */}
      <section id="debug" className={styles.section}>
        <h2 className={styles.sectionTitle}>Debug</h2>

        <FuncEntry name="printh" sig="(str)">
          <p className={styles.funcDesc}>Print a string to the browser console. Useful for debugging.</p>
        </FuncEntry>
      </section>
    </div>
  )
}
